import { AxiosResponse } from 'axios';
import ApiUrls from '../config/ApiUrls';
import { BillingFrequency } from '../config/Constants';
import { CurrencyCode } from '../config/CurrencyCode';
import { replaceByObject } from '../utils/Urls';
import API from './Api';

export type PriceList = {
  scheme: PricingScheme;
  plans: Map<Plan, Prices>;
};

export type Prices = {
  [currency in CurrencyCode]: Pricing;
};

export type PaddleTransactionApiResponse = {
  id: string;
};

export enum Plan {
  free = 'free',
  full = 'full',
  lite = 'lite',
  active = 'active',
  passive = 'passive',
  advanced = 'advanced',
}

export enum PricingScheme {
  _2022 = '_2022',
  _2023 = '_2023',
  _2023_10 = '_2023_10',
  _2024_06 = '_2024_06',
}

export const paddleClassicPricingSchemes: PricingScheme[] = [
  PricingScheme._2022,
  PricingScheme._2023,
  PricingScheme._2023_10,
];

export const singleEntityPlans: Plan[] = [
  Plan.full,
  Plan.lite
]

// For Paddle Billing pricing schemes (_2024_06 onwards), we need to check if the group is single entity to determine which plans apply
export function planIsApplicableToGroup(isSingleEntity: boolean, pricingScheme: PricingScheme, plan: Plan): boolean {
  // If the pricing scheme is Paddle Classic, all plans are applicable
  if (paddleClassicPricingSchemes.includes(pricingScheme)) {
    return true;
  }
  return (isSingleEntity && singleEntityPlans.includes(plan)) || (!isSingleEntity && !singleEntityPlans.includes(plan));
}

export type Pricing = {
  plan: Plan;
  currency: CurrencyCode;
  annual: number;
  monthly: number;
  pricingStructure: string;
  addons: number;
};

export type Addons = {
  recharger: number;
  brag: number;
};

export type SubscriptionApiResponse = {
  cancellationDate: string;
  cancelUrl: string;
  currency: string;
  nextBillDate: string;
  nextPaymentAmount: number;
  paddleSubscriptionId: number;
  pausedAt: string;
  pausedFrom: string;
  pausedReason: string;
  quantity: number;
  subscriptionPlanId: number;
  subscriptionStatus: string;
  billingFrequency: string;
  updateUrl: string;
  plan: string;
  pricingStructure: string;
  addons: Addons;
};

export type PaymentApiResponse = {
  id: number;
  created: string;
  currency: string;
  receiptUrl: string;
  saleGross: number;
  quantity: number;
  type: string;
};

export class BillingApi extends API {
  static getPricing(groupId: number, isSingleEntity: boolean): Promise<PriceList> {
    return API.get(replaceByObject(ApiUrls.Pricing, { groupId })).then(
      (response: AxiosResponse<Pricing[]>) => {
        const pricingScheme: PriceList = {
          scheme: response.data[0].pricingStructure as PricingScheme,
          plans: new Map(),
        };
        
        response.data
          .filter(pricing => planIsApplicableToGroup(isSingleEntity, pricingScheme.scheme, pricing.plan))
          .forEach(pricing => {
          if (!pricingScheme.plans.has(pricing.plan))
            pricingScheme.plans.set(pricing.plan, {} as Prices);

          pricingScheme.plans.get(pricing.plan)[pricing.currency] = pricing;
        });
        return pricingScheme;
      }
    );
  }

  static previewPrice(groupId: number, plan: Plan,
    billingFrequency: BillingFrequency,
    currency: CurrencyCode,
    rechargerAddonCount: number = 0,
    bragAddonCount: number = 0) {
    return API.post(
      replaceByObject(ApiUrls.Pricing, { groupId }),
      {
        plan,
        billingFrequency,
        currency,
        addons: {
          recharger: rechargerAddonCount,
          brag: bragAddonCount,
        },
      }
    ).then(
      (response: AxiosResponse<Pricing>) => {
        return response.data;
      }
    );
  }

  static getPayLink(groupId: number, plan: Plan, type: BillingFrequency) {
    return API.get(replaceByObject(ApiUrls.Paylink, { groupId }), {
      params: {
        billingFrequency: type,
        plan,
      },
    }).then((response: AxiosResponse<String>) => {
      return response.data;
    });
  }

  static getTransaction(
    groupId: number,
    plan: Plan,
    billingFrequency: BillingFrequency,
    currency: string,
    rechargerAddonCount: number,
    bragAddonCount: number,
  ): Promise<PaddleTransactionApiResponse> {
    return API.post(
      replaceByObject(ApiUrls.PaddleTransaction, { groupId }),
      {
        plan,
        billingFrequency,
        currency,
        addons: {
          recharger: rechargerAddonCount,
          brag: bragAddonCount,
        },
      }
    ).then((response: AxiosResponse<PaddleTransactionApiResponse>) => {
      return response.data;
    });
  }
}
